import axios, { AxiosError } from 'axios';

export interface ApiError {
  success: false;
  error: string;
  error_code?: string;
  details?: any;
}

export interface ApiResponse<T = any> {
  success: true;
  data: T;
  message?: string;
}

export type ApiResult<T = any> = ApiResponse<T> | ApiError;

// Error codes mapping for user-friendly messages
const ERROR_MESSAGES: Record<string, string> = {
  INTERNAL_SERVER_ERROR: 'An unexpected error occurred. Please try again.',
  RESOURCE_NOT_FOUND: 'The requested resource was not found.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  NETWORK_ERROR: 'Network connection error. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  BAD_REQUEST: 'Invalid request. Please check your input.',
  METHOD_NOT_ALLOWED: 'This action is not allowed.',
  UNSUPPORTED_FORMAT: 'Unsupported format requested.',
  UNPERMITTED_PARAMETERS: 'Invalid parameters provided.',
  JWT_ERROR: 'Authentication failed. Please log in again.',
  PARAMETER_MISSING: 'Required information is missing.',
};

// HTTP status code mapping
const STATUS_MESSAGES: Record<number, string> = {
  400: 'Bad request. Please check your input.',
  401: 'Authentication required. Please log in.',
  403: 'Access denied. You don\'t have permission for this action.',
  404: 'Resource not found.',
  405: 'Method not allowed.',
  406: 'Unsupported format.',
  422: 'Validation failed. Please check your input.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again later.',
  502: 'Bad gateway. Please try again later.',
  503: 'Service unavailable. Please try again later.',
  504: 'Gateway timeout. Please try again later.',
};

export class ApiErrorHandler {
  static isApiError(response: any): response is ApiError {
    return response && response.success === false && response.error;
  }

  static getErrorMessage(error: any): string {
    // Handle Axios errors
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      
      // Check if server returned a structured error
      if (axiosError.response?.data && this.isApiError(axiosError.response.data)) {
        const apiError = axiosError.response.data;
        return ERROR_MESSAGES[apiError.error_code || ''] || apiError.error;
      }
      
      // Handle HTTP status codes
      if (axiosError.response?.status) {
        return STATUS_MESSAGES[axiosError.response.status] || 'An error occurred.';
      }
      
      // Handle network errors
      if (axiosError.code === 'NETWORK_ERROR') {
        return ERROR_MESSAGES.NETWORK_ERROR;
      }
      
      // Handle timeout errors
      if (axiosError.code === 'ECONNABORTED') {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
    }
    
    // Handle generic errors
    if (error?.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred.';
  }

  static getErrorCode(error: any): string | undefined {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ApiError>;
      
      if (axiosError.response?.data && this.isApiError(axiosError.response.data)) {
        return axiosError.response.data.error_code;
      }
      
      return axiosError.response?.status?.toString();
    }
    
    return undefined;
  }

  static isAuthenticationError(error: any): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      return status === 401 || status === 403;
    }
    
    const errorCode = this.getErrorCode(error);
    return errorCode === 'JWT_ERROR' || errorCode === 'UNAUTHORIZED';
  }

  static isNetworkError(error: any): boolean {
    if (axios.isAxiosError(error)) {
      return !error.response || error.code === 'NETWORK_ERROR' || error.code === 'ECONNABORTED';
    }
    
    return false;
  }

  static isServerError(error: any): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      return status ? status >= 500 : false;
    }
    
    return false;
  }

  static isClientError(error: any): boolean {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      return status ? status >= 400 && status < 500 : false;
    }
    
    return false;
  }

  static shouldRetry(error: any): boolean {
    // Retry on network errors or server errors (5xx)
    return this.isNetworkError(error) || this.isServerError(error);
  }

  static getRetryDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, etc.
    return Math.min(1000 * Math.pow(2, attempt), 30000);
  }

  static async handleApiCall<T>(
    apiCall: () => Promise<T>,
    options: {
      maxRetries?: number;
      onError?: (error: any) => void;
      onRetry?: (attempt: number, error: any) => void;
    } = {}
  ): Promise<T | null> {
    const { maxRetries = 3, onError, onRetry } = options;
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await apiCall();
      } catch (error) {
        lastError = error;
        
        // Don't retry authentication errors or client errors (4xx except 429)
        if (this.isAuthenticationError(error) || (this.isClientError(error) && !this.shouldRetry(error))) {
          if (onError) onError(error);
          return null;
        }
        
        // Don't retry on last attempt
        if (attempt === maxRetries) {
          if (onError) onError(error);
          return null;
        }
        
        // Retry with exponential backoff
        if (this.shouldRetry(error)) {
          const delay = this.getRetryDelay(attempt);
          if (onRetry) onRetry(attempt + 1, error);
          
          await new Promise(resolve => setTimeout(resolve, delay));
        } else {
          if (onError) onError(error);
          return null;
        }
      }
    }
    
    if (onError) onError(lastError);
    return null;
  }
}

// Higher-order function for wrapping API calls with error handling
export const withApiErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  options: {
    maxRetries?: number;
    onError?: (error: any) => void;
    onRetry?: (attempt: number, error: any) => void;
  } = {}
): Promise<T | null> => {
  const result = await ApiErrorHandler.handleApiCall(apiCall, options);
  
  // If result is an axios response, return the response data
  if (result && typeof result === 'object' && 'data' in result && 'status' in result) {
    return (result as any).data;
  }
  
  return result;
};

// Hook for handling API errors in components
export const useApiErrorHandler = () => {
  const handleError = (error: any, customMessage?: string): string => {
    const message = customMessage || ApiErrorHandler.getErrorMessage(error);
    
    // Log error for debugging
    console.error('API Error:', error);
    
    return message;
  };

  const handleAuthenticationError = (error: any) => {
    if (ApiErrorHandler.isAuthenticationError(error)) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  return {
    handleError,
    handleAuthenticationError,
    isAuthenticationError: ApiErrorHandler.isAuthenticationError,
    isNetworkError: ApiErrorHandler.isNetworkError,
    isServerError: ApiErrorHandler.isServerError,
    shouldRetry: ApiErrorHandler.shouldRetry,
  };
};